// Van der Linden RL/RI 16 — score calculation
// Source: "Normes Grober_Bushke_VDL_calcul.xls"
//
// Inputs: age (years), nsc (niveau socio-culturel 1-3), gender (H/F)
// Raw scores (max 16 each):
//   RL1, RL2, RL3, RLD       (rappel libre)
//   RIM, RT1, RT2, RT3, RTD  (rappel total = libre + indicé)
//
// Rappel libre: regression-based expected mean per (age, NSC, gender),
// then z-score and percentile (NORMSDIST). Classification:
//   > -1ET → Normal, > -2ET → Médiocre, else → Déficitaire.
//
// Rappel total: fixed centile bin lookup per raw score.

const toNum = (v) => {
  if (v == null || v === "") return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Age group D10: >74→5, >64→4, >49→3, >29→2, else→1
function ageGroup(age) {
  if (age > 74) return 5;
  if (age > 64) return 4;
  if (age > 49) return 3;
  if (age > 29) return 2;
  return 1;
}

// Sex group D12: F→1, else→2
function sexGroup(gender) {
  return gender === "F" ? 1 : 2;
}

// Standard normal CDF (Abramowitz & Stegun 26.2.17), good to ~7 decimals.
function normsdist(z) {
  if (!Number.isFinite(z)) return null;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429;
  const erf = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * erf);
}

// Means for rappel libre (RL1, RL2, RL3, RLD) given ageGroup g, NSC n, sexGroup s.
const RL_MEAN = {
  RL1: (g, n, s) => 12    - 0.8  * g + 0.2  * n - 0.52 * s,
  RL2: (g, n, s) => 14.01 - 0.95 * g + 0.37 * n - 0.68 * s,
  RL3: (g, n, s) => 15.19 - 0.99 * g + 0.39 * n - 0.5  * s,
  RLD: (g, n, s) => 16.12 - 1.08 * g + 0.35 * n - 0.72 * s,
};

// Standard deviation per metric (1ET drop from mean).
const RL_SD = { RL1: 2.18, RL2: 2.27, RL3: 2.23, RLD: 2.20 };

function classify(score, mean, sd) {
  if (score > mean - sd) return "Normal";
  if (score > mean - 2 * sd) return "Médiocre";
  return "Déficitaire";
}

// Rappel total centile lookups (mirroring Excel formulas).
function centileRIM(s) {
  if (s === 16) return "C50";
  if (s === 15) return "C25";
  if (s === 14) return "C5-25";
  if (s === 13) return "C5";
  if (s === 12) return "C1-C5";
  if (s === 11) return "C1";
  return "<C1";
}
function centileRT1(s) {
  if (s === 16) return "C75";
  if (s === 15) return "C50";
  if (s === 14) return "C25";
  if (s === 13) return "C5-C25";
  if (s === 12) return "C5";
  if (s > 8)    return "C1-C5";
  if (s === 8)  return "C1";
  return "<C1";
}
function centileRT2(s) {
  if (s === 16) return "C50";
  if (s === 15) return "C25";
  if (s === 14) return "C5-25";
  if (s === 13) return "C5";
  if (s > 9.8)  return "C1-C5";
  if (s === 9.8) return "C1";
  return "<C1";
}
function centileRT3(s) {
  if (s === 16) return "C25";
  if (s === 15) return "C5-C25";
  if (s === 14) return "C5";
  if (s === 13) return "C1-C5";
  if (s === 12) return "C1";
  return "<C1";
}
function centileRTD(s) {
  if (s === 16) return "C25";
  if (s === 15) return "C5-C25";
  if (s === 14) return "C5";
  if (s > 11.8) return "C1-C5";
  return "<C1";
}

const RT_LOOKUP = {
  RIM: centileRIM, RT1: centileRT1, RT2: centileRT2, RT3: centileRT3, RTD: centileRTD,
};

/**
 * @param {Object} input
 * @param {"H"|"F"} input.gender
 * @param {number}  input.age
 * @param {1|2|3}   input.nsc
 * @param {number}  [input.RL1] [input.RL2] [input.RL3] [input.RLD]
 * @param {number}  [input.RIM] [input.RT1] [input.RT2] [input.RT3] [input.RTD]
 */
export function calcVdlRLRI16(input) {
  const gender = (input.gender || "").toString().toUpperCase();
  if (gender !== "H" && gender !== "F") return { error: "Genre requis (H ou F)" };

  const nsc = parseInt(input.nsc, 10);
  if (![1, 2, 3].includes(nsc)) return { error: "NSC requis (1-3)" };

  const age = toNum(input.age);
  if (age == null) return { error: "Âge requis" };

  const g = ageGroup(age);
  const s = sexGroup(gender);
  const scores = {};

  for (const key of ["RL1", "RL2", "RL3", "RLD"]) {
    const raw = toNum(input[key]);
    if (raw == null) continue;
    const mean = RL_MEAN[key](g, nsc, s);
    const sd = RL_SD[key];
    const z = (raw - mean) / sd;
    const percentile = normsdist(z) * 100;
    scores[key] = {
      brut: raw,
      mean: Math.round(mean * 100) / 100,
      z: Math.round(z * 100) / 100,
      percentile: Math.round(percentile * 10) / 10,
      classification: classify(raw, mean, sd),
    };
  }

  for (const key of ["RIM", "RT1", "RT2", "RT3", "RTD"]) {
    const raw = toNum(input[key]);
    if (raw == null) continue;
    scores[key] = { brut: raw, centile: RT_LOOKUP[key](raw) };
  }

  return { gender, age, nsc, scores };
}

/**
 * Convert a VdL centile bin ("<C1", "C1", "C1-C5", "C5", "C5-25", "C5-C25",
 * "C25", "C50", "C75") to a PC-style string ("< 1", "1", "1-5", "5", "5-25",
 * "25", "50", "75").
 */
export function vdlCentileToPCStr(centile) {
  if (!centile) return "";
  if (centile === "<C1") return "< 1";
  return centile.replace(/C/g, "").replace(/^-/, "");
}

/**
 * Format a percentile (0-100) to a short PC-style string.
 *   < 1   if pc < 1
 *   > 99  if pc > 99
 *   else integer
 */
export function vdlPercentileToPCStr(pc) {
  if (pc == null || !Number.isFinite(pc)) return "";
  if (pc < 1) return "< 1";
  if (pc > 99) return "> 99";
  return String(Math.round(pc));
}
